// Cart persisted in sessionStorage (per-tab). This intentionally prevents
// a station chosen in a previous visit from leaking into a new one.
// Also purges any legacy localStorage entries left over from prior builds.
const KEY = "sb_cart_v1";

// One-time migration: remove any stale localStorage entries related to
// this app (older builds used localStorage; a fresh visit must never
// resurrect that state).
try {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem(KEY);
    const toDel = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith("sb_waiter_until_")) toDel.push(k);
    }
    toDel.forEach((k) => window.localStorage.removeItem(k));
  }
} catch { /* ignore */ }

function _store() {
  try { return window.sessionStorage; } catch { return null; }
}

const EMPTY = { station: null, items: [] };

export function readCart() {
  const s = _store();
  if (!s) return { ...EMPTY };
  try {
    return JSON.parse(s.getItem(KEY) || JSON.stringify(EMPTY));
  } catch { return { ...EMPTY }; }
}

export function writeCart(cart) {
  const s = _store();
  if (!s) return;
  s.setItem(KEY, JSON.stringify(cart));
  window.dispatchEvent(new Event("sb-cart-change"));
}

export function clearCart() {
  const s = _store();
  if (!s) return;
  s.removeItem(KEY);
  window.dispatchEvent(new Event("sb-cart-change"));
}

export function setStation(station) {
  const c = readCart();
  if (!c.station || c.station.id !== station.id) c.items = [];
  c.station = station;
  writeCart(c);
}

export function addItem(product, quantity, modifiers, note) {
  const c = readCart();
  c.items.push({
    uid: crypto.randomUUID(),
    product_id: product.id,
    product_name: product.name,
    unit_price_cents: product.price_cents,
    quantity,
    modifiers: modifiers || [],
    note: note || null,
  });
  writeCart(c);
}

export function removeItem(uid) {
  const c = readCart();
  c.items = c.items.filter((i) => i.uid !== uid);
  writeCart(c);
}

export function updateItemQty(uid, qty) {
  const c = readCart();
  c.items = c.items.map((i) => (i.uid === uid ? { ...i, quantity: Math.max(1, qty) } : i));
  writeCart(c);
}

export function cartTotals(cart, opts = {}) {
  const {
    serviceChargeCents = 0,
    copertoCents = 0,
    peopleCount = 0,
    copertoCategoryIds = [],
    categoryByProductId = {},
  } = typeof opts === "number" ? { serviceChargeCents: opts } : opts;
  let subtotal = 0;
  for (const it of cart.items) {
    let unit = it.unit_price_cents;
    for (const m of it.modifiers || []) unit += m.price_delta_cents || 0;
    subtotal += unit * it.quantity;
  }
  const copSet = new Set(copertoCategoryIds || []);
  const needsCoperto = cart.items.some((it) => copSet.has(categoryByProductId[it.product_id]));
  const people = needsCoperto ? Math.max(0, parseInt(peopleCount) || 0) : 0;
  const coperto = needsCoperto ? copertoCents * people : 0;
  return {
    subtotal,
    service: serviceChargeCents,
    coperto,
    copertoUnit: needsCoperto ? copertoCents : 0,
    people,
    needsCoperto,
    total: subtotal + serviceChargeCents + coperto,
  };
}
